import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  DataTable,
  EmptyState,
  PageContainer,
  PageHeader,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { menuApi } from './menuApi';
import MenuNavTabs from './MenuNavTabs';

const money = (cur, n) => `${cur || 'USD'} ${formatAmount(n)}`;

const period = (a) => {
  const f = (d) => (d ? new Date(d).toLocaleDateString() : null);
  const s = f(a.periodStart);
  const e = f(a.periodEnd);
  if (s && e) return `${s} – ${e}`;
  return s || e || '—';
};

export default function MenuEngineeringPage() {
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission } = useProperty();
  const canManage = hasPermission('recipes.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await menuApi.list()) || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'number', header: t('Ref'), width: 120, render: (a) => <span style={{ fontFamily: 'var(--font-mono)' }}>{a.number}</span> },
    { key: 'name', header: t('Analysis'), render: (a) => <span style={{ fontWeight: 600 }}>{a.name}</span> },
    { key: 'period', header: t('Period'), width: 200, render: period },
    { key: 'items', header: t('Items'), width: 80, align: 'right', render: (a) => a.itemCount },
    { key: 'contribution', header: t('Total contribution'), width: 170, align: 'right', render: (a) => money(a.currency, a.totalContribution) },
    { key: 'updated', header: t('Updated'), width: 130, render: (a) => (a.updatedAt ? new Date(a.updatedAt).toLocaleDateString() : '—') },
  ];

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Menu engineering')}
        description={t('Classify menu items by popularity and profitability (Star / Plowhorse / Puzzle / Dog) using live recipe costs and sales mix.')}
        icon={<Icon.LayoutGrid size={22} />}
        actions={
          canManage && (
            <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/menu-engineering/new')}>
              {t('New analysis')}
            </Button>
          )
        }
      />

      <MenuNavTabs active="menu" />

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(a) => a.id}
          density="comfortable"
          onRowClick={(a) => navigate(`/menu-engineering/${a.id}`)}
          emptyState={
            <EmptyState
              icon={<Icon.LayoutGrid size={28} />}
              title={t('No analyses yet')}
              description={t('Build a menu-engineering study from your menu items and the units sold in a period.')}
              action={canManage && <Button variant="primary" onClick={() => navigate('/menu-engineering/new')}>{t('New analysis')}</Button>}
            />
          }
        />
      )}
    </PageContainer>
  );
}
